// HELPER ACTIONS

import {
  setLoadingRoutesList,
  setLoadingProject,
  setLoadingStop,
  setLoadingUnscheduled,
  setInitialProjectLoading
} from "./loading";
import API from "../../utils/axios";
import {
  setVehicles,
  setSelectedVehicles,
  setProject,
  setChangedVehicles,
  setShowingRouteId
} from "./project";
import { setSolution } from "./solution";
import { enqueueSnackbar } from "./snackbars";
import {
  setCurrentVehicleSelection,
  setDragSelectionByArr,
  setInfoDialog,
  setManualDragSelection
} from "./common";
import {
  setExtendableType,
  setExtendableId,
  setExtendableSaveDisabled,
  reopenExtendable
} from "./edit";
import {
  timeToSeconds,
  getHoursMins,
  getFormattedPhoneAndCountry,
  isEmptyObj,
  makeTextPlural
} from "../../utils/helpers";
import { setHoverStop } from "./map";
import { client } from "../../components/map/DriverMarkers";
import { findRouteIndexById, sendReorderWSMessage } from "./dragAndDrop";
import { notifyDriversOfReopenedRoutes } from "./reopenNotify";
import { applyReopenedRoutes } from "./reopenMerge";
import mixpanelWrapper from "../../utils/mixpanel";
import { SELECTED_VEHICLES } from "./types";
import { PhoneNumberUtil } from "google-libphonenumber";

const phoneNumberUtil = PhoneNumberUtil.getInstance();

const runPromisesInBatches = async (batchItems, batchSize, itemToPromise) => {
  const allSettledResults = [];
  for (let startIndex = 0; startIndex < batchItems.length; startIndex += batchSize) {
    const batch = batchItems.slice(startIndex, startIndex + batchSize);
    const promises = batch.map(itemToPromise).filter(Boolean);
    if (promises.length) {
      const settled = await Promise.allSettled(promises);
      allSettledResults.push(...settled);
    }
  }
  return allSettledResults;
};

// Set or unset the POD photo requirement for all stops in the provided routes.
export const setPodPhotoRequirementForRoutes = (selectedRouteIds, enablePodPhoto) => (
  dispatch,
  getState
) => {
  const project = getState().project;
  const solution = project.solution || {};

  if (!solution.routes || !Array.isArray(selectedRouteIds) || !selectedRouteIds.length) {
    return
  }

  // Collect stop ids from the selected routes (exclude non-service stops, like start/end/break).
  const stopIds = [];
  selectedRouteIds.forEach(routeId => {
    const route = solution.routes.find(routeItem => routeItem.id === routeId);
    if (route && route.stops && route.stops.length) {
      route.stops.forEach(stop => {
        if (!["start", "end", "break"].includes(stop.type)) {
          stopIds.push(stop.id);
        }
      });
    }
  });

  if (!stopIds.length) {
    dispatch(enqueueSnackbar({ message: "No service stops found for selected routes." }));
    return;
  }

  dispatch(setLoadingRoutesList(true));

  // Prepare mapping of stop IDs to payloads, validating phone numbers and removing invalid phone
  const stopPayloads = stopIds.map(stopId => {
    const currentStop = project.vrp.stops.find(stop => stop.id === stopId) ||
      project.solution.routes
        .map(route => route.stops.find(stop => stop.id === stopId))
        .find(Boolean);

    if (!currentStop) return null;

    const newStopPayload = {
      ...currentStop,
      requirements: { ...(currentStop.requirements || {}), picture: !!enablePodPhoto }
    };

    // Validate phone: remove recipient_phone if it's an invalid phone number
    try {
      const phone = newStopPayload.information && newStopPayload.information.recipient_phone;
      if (phone) {
        const normalized = phone.toString().startsWith("+") ? phone.toString() : `+${phone.toString()}`;
        const parsed = phoneNumberUtil.parse(normalized);
        if (!phoneNumberUtil.isValidNumber(parsed)) {
          newStopPayload.information = { ...(newStopPayload.information || {}), recipient_phone: null };
        }
      }
    } catch (e) {
      newStopPayload.information = { ...(newStopPayload.information || {}), recipient_phone: null };
    }

    return { id: stopId, payload: newStopPayload };
  }).filter(Boolean);

  runPromisesInBatches(
    stopPayloads,
    10,
    payloadItem => API.patch(`/v1/projects/${project.id}/services/${payloadItem.id}`, payloadItem.payload)
  )
    .then(settledResults => {
      const successfulStopIds = [];
      let settledIndex = 0;
      for (let stopIndex = 0; stopIndex < stopIds.length; stopIndex++) {
        const stopId = stopIds[stopIndex];
        const exists = stopPayloads.find(payload => payload.id === stopId);
        if (!exists) continue;
        const settledResult = settledResults[settledIndex];
        settledIndex += 1;
        if (settledResult && settledResult.status === "fulfilled") successfulStopIds.push(stopId);
      }

      const updatedProject = { ...project };

      if (successfulStopIds.length) {
        if (updatedProject.vrp && Array.isArray(updatedProject.vrp.stops)) {
          updatedProject.vrp.stops = updatedProject.vrp.stops.map(stop =>
            successfulStopIds.includes(stop.id)
              ? { ...stop, requirements: { ...(stop.requirements || {}), picture: !!enablePodPhoto } }
              : stop
          );
        }

        if (updatedProject.solution && Array.isArray(updatedProject.solution.routes)) {
          const newSolution = { ...updatedProject.solution };
          newSolution.routes = newSolution.routes.map(route => ({
            ...route,
            stops: route.stops.map(stop =>
              successfulStopIds.includes(stop.id)
                ? { ...stop, requirements: { ...(stop.requirements || {}), picture: !!enablePodPhoto } }
                : stop
            )
          }));
          updatedProject.solution = newSolution;
        }

        dispatch(setProject(updatedProject));
      }

      if (successfulStopIds.length === stopIds.length) {
        dispatch(
          enqueueSnackbar({
            message: `Stop${stopIds.length === 1 ? "" : "s"} updated — POD photo ${enablePodPhoto ? "enabled" : "disabled"}.`
          })
        );
      } else if (successfulStopIds.length > 0) {
        dispatch(
          enqueueSnackbar({
            message: `${successfulStopIds.length} of ${stopIds.length} stop${makeTextPlural(successfulStopIds.length)} updated — POD photo ${enablePodPhoto ? "enabled" : "disabled"}.`
          })
        );
      } else {
        dispatch(
          enqueueSnackbar({ message: "No stops could be updated — something went wrong." })
        );
      }
    })
    .catch(err => {
      console.error(err);
      dispatch(enqueueSnackbar({ message: "Something went wrong while updating POD requirements." }));
    })
    .finally(() => {
      dispatch(setLoadingRoutesList(false));
    });
};

// Reopen (reset to "to-do"/optimized) the given finished/skipped stops. The backend reverses the finish
// side-effects; here we optimistically reset the stop status locally on success (mirrors the POD-photo flow).
export const reopenStops = (stopIds = [], routeId) => (dispatch, getState) => {
  if (!Array.isArray(stopIds) || !stopIds.length) return;

  const project = getState().project;
  dispatch(setLoadingRoutesList(true));

  API.post(`/v1/projects/${project.id}/services/reopen`, stopIds)
    .then(res => {
      // Reflect the authoritative reopened state (reset statuses + recomputed ETAs) returned by the API.
      dispatch(setProject(applyReopenedRoutes(project, res.data)));
      mixpanelWrapper.track("Reopen stops", { Count: stopIds.length, RouteId: routeId });
      dispatch(
        enqueueSnackbar({
          message: `${stopIds.length} stop${makeTextPlural(stopIds.length)} reopened.`
        })
      );

      // Notify the driver app(s) for each affected route — reuse the live-adjustment relay path
      // (same {type:"route_reorder"} signal as drag/reorder); dwa refetches and reflects the reopen.
      notifyDriversOfReopenedRoutes(res.data);
    })
    .catch(err => {
      console.error(err);
      dispatch(enqueueSnackbar({ message: "Something went wrong while reopening the stop(s)." }));
    })
    .finally(() => {
      dispatch(setLoadingRoutesList(false));
    });
};

// Reopen (reset to in-progress) the given finished routes so stops can be added to them again.
export const reopenRoutes = (routeIds = []) => (dispatch, getState) => {
  if (!Array.isArray(routeIds) || !routeIds.length) return;

  const project = getState().project;
  dispatch(setLoadingRoutesList(true));

  API.post(`/v1/projects/${project.id}/routes/reopen`, routeIds)
    .then(res => {
      // Reflect the authoritative reopened state: every stop reset to to-do with recomputed ETAs, so the
      // route view updates immediately without a manual refresh (the bug being fixed here).
      dispatch(setProject(applyReopenedRoutes(project, res.data)));
      mixpanelWrapper.track("Reopen routes", { Count: routeIds.length });
      dispatch(
        enqueueSnackbar({
          message: `${routeIds.length} route${makeTextPlural(routeIds.length)} reopened.`
        })
      );

      // Notify the driver app(s) for each reopened route via the live-adjustment relay path.
      notifyDriversOfReopenedRoutes(res.data);
    })
    .catch(err => {
      console.error(err);
      dispatch(enqueueSnackbar({ message: "Something went wrong while reopening the route(s)." }));
    })
    .finally(() => {
      dispatch(setLoadingRoutesList(false));
    });
};

// Set or unset the POD photo requirement for the provided stop ids.
export const setPodPhotoRequirementForStops = (selectedStopIds = [], enablePodPhoto) => (
  dispatch,
  getState
) => {
  const project = getState().project;

  if (!Array.isArray(selectedStopIds) || !selectedStopIds.length) {
    dispatch(enqueueSnackbar({ message: "No stops selected." }));
    return;
  }

  dispatch(setLoadingRoutesList(true));

  const stopPayloads = selectedStopIds.map(stopId => {
    const currentStop =
      project.vrp.stops.find(stop => stop.id === stopId) ||
      project.solution.routes
        .map(route => route.stops.find(stop => stop.id === stopId))
        .find(Boolean);

    if (!currentStop) return null;

    const newStopPayload = {
      ...currentStop,
      requirements: { ...(currentStop.requirements || {}), picture: !!enablePodPhoto }
    };

    try {
      const phone = newStopPayload.information && newStopPayload.information.recipient_phone;
      if (phone) {
        const normalized = phone.toString().startsWith("+") ? phone.toString() : `+${phone.toString()}`;
        const parsed = phoneNumberUtil.parse(normalized);
        if (!phoneNumberUtil.isValidNumber(parsed)) {
          newStopPayload.information = { ...(newStopPayload.information || {}), recipient_phone: null };
        }
      }
    } catch (e) {
      newStopPayload.information = { ...(newStopPayload.information || {}), recipient_phone: null };
    }

    return { id: stopId, payload: newStopPayload };
  }).filter(Boolean);

  runPromisesInBatches(
    stopPayloads,
    10,
    payloadItem => API.patch(`/v1/projects/${project.id}/services/${payloadItem.id}`, payloadItem.payload)
  )
    .then(settledResults => {
      const successfulStopIds = [];
      let settledIndex = 0;
      for (let selectedIndex = 0; selectedIndex < selectedStopIds.length; selectedIndex++) {
        const selectedStopId = selectedStopIds[selectedIndex];
        const exists = stopPayloads.find(p => p.id === selectedStopId);
        if (!exists) continue;
        const settledResult = settledResults[settledIndex];
        settledIndex += 1;
        if (settledResult && settledResult.status === "fulfilled") successfulStopIds.push(selectedStopId);
      }

      const updatedProject = { ...project };

      if (successfulStopIds.length) {
        if (updatedProject.vrp && Array.isArray(updatedProject.vrp.stops)) {
          updatedProject.vrp.stops = updatedProject.vrp.stops.map(stop =>
            successfulStopIds.includes(stop.id)
              ? { ...stop, requirements: { ...(stop.requirements || {}), picture: !!enablePodPhoto } }
              : stop
          );
        }

        if (updatedProject.solution && Array.isArray(updatedProject.solution.routes)) {
          const newSolution = { ...updatedProject.solution };
          newSolution.routes = newSolution.routes.map(route => ({
            ...route,
            stops: route.stops.map(stop =>
              successfulStopIds.includes(stop.id)
                ? { ...stop, requirements: { ...(stop.requirements || {}), picture: !!enablePodPhoto } }
                : stop
            )
          }));
          updatedProject.solution = newSolution;
        }

        dispatch(setProject(updatedProject));
      }

      if (successfulStopIds.length === selectedStopIds.length) {
        dispatch(
          enqueueSnackbar({
            message: `Stop${selectedStopIds.length === 1 ? "" : "s"} updated — POD photo ${enablePodPhoto ? "enabled" : "disabled"}.`
          })
        );
      } else if (successfulStopIds.length > 0) {
        dispatch(
          enqueueSnackbar({
            message: `${successfulStopIds.length} of ${selectedStopIds.length} stop${makeTextPlural(successfulStopIds.length)} updated — POD photo ${enablePodPhoto ? "enabled" : "disabled"}.`
          })
        );
      } else {
        dispatch(
          enqueueSnackbar({ message: "No stops could be updated — something went wrong." })
        );
      }
    })
    .catch(err => {
      console.error(err);
      dispatch(enqueueSnackbar({ message: "Something went wrong while updating POD requirements." }));
    })
    .finally(() => {
      dispatch(setLoadingRoutesList(false));
    });
};

export const cleanSolutionWithNewRoutes = (solution, newRoutes) => {
  if (!solution || !Array.isArray(solution.routes) || !Array.isArray(newRoutes) || newRoutes.length === 0) {
    return solution;
  }

  const getBaseVehicleId = vehicleId => (vehicleId || "").split("_")[0];

  const originalRoutes = solution.routes;
  const affectedBaseVehicleIds = new Set(
    newRoutes.map(route => getBaseVehicleId(route.vehicle_id))
  );
  const newRouteIds = new Set(newRoutes.map(route => route && route.id));

  const preservedRoutes = [];
  let anchorFound = false;
  let insertionIndex = 0;
  for (let i = 0; i < originalRoutes.length; i++) {
    const originalRoute = originalRoutes[i];
    const baseVehicleId = getBaseVehicleId(originalRoute.vehicle_id);
    const isAffectedByVehicle = affectedBaseVehicleIds.has(baseVehicleId);
    if (isAffectedByVehicle && !anchorFound) {
      insertionIndex = preservedRoutes.length;
      anchorFound = true;
    }
    if (!isAffectedByVehicle || newRouteIds.has(originalRoute.id)) {
      preservedRoutes.push(originalRoute);
    }
  }

  if (!anchorFound) {
    insertionIndex = preservedRoutes.length;
  }

  const preservedIndexByRouteId = new Map();
  preservedRoutes.forEach((preservedRoute, index) =>
    preservedIndexByRouteId.set(preservedRoute.id, index)
  );
  const brandNewRoutes = [];
  newRoutes.forEach(newRoute => {
    if (preservedIndexByRouteId.has(newRoute.id)) {
      preservedRoutes[preservedIndexByRouteId.get(newRoute.id)] = newRoute;
    } else {
      brandNewRoutes.push(newRoute);
    }
  });

  if (brandNewRoutes.length) {
    preservedRoutes.splice(insertionIndex, 0, ...brandNewRoutes);
  }

  solution.routes = preservedRoutes;
  return solution;
};

/** Lock/unlock route
 * @param {string} id Route id
 * @param {string} vehicle_id Route's vehicle group id
 * @param {boolean} locked Current locked/unlocked state of the route
 */
export const handleRouteLock =
  (id, vehicle_id, locked) => (dispatch, getState) => {
    const { solution, vehicles } = getState().project;
    dispatch(setLoadingRoutesList(true));
    API.patch(`/v1/projects/${solution.id}/routes/${id}/lock`)
      .then(res => {
        const realVehicleId = vehicle_id.split("_")[0];
        const newSolution = { ...solution };
        const newRoutes = solution.routes.map(route => {
          if (route.vehicle_id.split("_")[0] === realVehicleId) {
            let newRoute = { ...route };
            newRoute.locked = !newRoute.locked;
            return newRoute;
          }
          return route;
        });
        newSolution.routes = newRoutes;
        dispatch(setSolution(newSolution));
        const vehicleIndex = vehicles.findIndex(
          vehicle => vehicle.vehicle_id === realVehicleId
        );
        vehicles[vehicleIndex].locked = !vehicles[vehicleIndex].locked;

        dispatch(setVehicles(vehicles));
        dispatch(setLoadingRoutesList(false));
      })
      .catch(err => {
        dispatch(setLoadingRoutesList(false));
        dispatch(
          enqueueSnackbar({
            message: "Something went wrong, couldn't lock route"
          })
        );
        console.log(err);
      });
  };

/** Lock/unlock routes
 */
export const handleRoutesLock =
  (routeIds, mixedLockedAndUnlocked) => (dispatch, getState) => {
    const { solution, vehicles } = getState().project;

    dispatch(setLoadingRoutesList(true));

    let selectedRoutes = routeIds
      .map(routeId => solution.routes.find(route => route.id === routeId))
      .filter(route => (mixedLockedAndUnlocked ? route.locked : true));

    console.log(mixedLockedAndUnlocked, selectedRoutes);

    Promise.allSettled(
      selectedRoutes.map(route =>
        API.patch(`/v1/projects/${solution.id}/routes/${route.id}/lock`)
      )
    ).then(results => {
      const newSolution = { ...solution };
      results.forEach((result, resultIndex) => {
        const route = selectedRoutes[resultIndex];
        if (result.status === "fulfilled") {
          const realVehicleId = route.vehicle_id.split("_")[0];
          newSolution.routes.forEach((originalRoute, originalRouteIndex) => {
            if (originalRoute.vehicle_id.split("_")[0] === realVehicleId) {
              newSolution.routes[originalRouteIndex].locked =
                !newSolution.routes[originalRouteIndex].locked;
            }
          });

          const vehicleIndex = vehicles.findIndex(
            vehicle => vehicle.vehicle_id === realVehicleId
          );
          vehicles[vehicleIndex].locked = !vehicles[vehicleIndex].locked;
        } else if (result.status === "rejected") {
          dispatch(
            enqueueSnackbar({
              message: `Something went wrong, couldn't lock ${route.vehicle_name} route.`
            })
          );
        }
      });
      dispatch(setSolution(newSolution));
      dispatch(setLoadingRoutesList(false));
    });
  };

/** Update vehicle with given changes
 * @param {object} inputValues Fields with updated values
 * @param {boolean} shouldEditName Indicates whether name and capacity also updating(freemium only) to make corresponding request
 */
export const saveVehicleChanges = inputValues => (dispatch, getState) => {
  const handleClose = () => {
    dispatch(setCurrentVehicleSelection([]));
    dispatch(setExtendableType(null));
    dispatch(setExtendableId(null));
  };

  let bodyArr = [];
  const editId = getState().extendable.id;
  const project = getState().project;
  const vehicles = project.vehicles;
  const solution = project.solution;

  const { configuration } = getState().project.vrp;

  const vehicle =
    vehicles.find(el => {
      return el.vehicle_id === editId;
    }) || "";

  //When our vehicles disabled it is not in our vehicles and we don't need find and update just need to make post request for vehicle and get new address ;
  // Changes single vehicle id for in current vehicle selection it is good idea cause they share the same logic
  const currentVehicleSelection =
    // If multiple mode take currentVehicleSelecton with vehicle id's
    editId === "multiple"
      ? getState().common.currentVehicleSelection
      : // if vehicle is not found in vehicles it means that our vehilce is disabled and sended just type_id of disabled vehicle
      !vehicle
        ? [editId]
        : [];

  const vehicleTypes = project.vehicleTypes;

  // if multiple edit
  if (currentVehicleSelection.length) {
    // create request payload for current selection
    bodyArr = currentVehicleSelection.map(vehicleTypeId => {
      let vehicleType;

      vehicleType = vehicleTypes.find((item, i) => {
        if (item?.id === vehicleTypeId) {
          return true;
        }
        return false;
      });

      const body = {
        type_id: vehicleType.id,
        start_address: inputValues.startLocation || configuration.depot_address,
        end_address: inputValues.endLocation,
        earliest_start: timeToSeconds(getHoursMins(inputValues.startTime)),
        latest_end: timeToSeconds(getHoursMins(inputValues.endTime)),
        break: {
          earliest: inputValues.startBreak
            ? timeToSeconds(getHoursMins(inputValues.startBreak))
            : null,
          latest: inputValues.endBreak
            ? timeToSeconds(getHoursMins(inputValues.endBreak))
            : null,
          duration: inputValues.breakDuration
            ? Number(inputValues.breakDuration)
            : null
        }
      };

      return body;
    });
  } else {
    // editing single vehicle
    let vehicleType = vehicleTypes.find((item, i) => {
      if (item.id === vehicle?.type_id?.id) {
        return true;
      }
      return false;
    });

    bodyArr = [
      {
        id: vehicle ? vehicle.vehicle_id : null,
        type_id: vehicleType.id,
        start_address: inputValues.startLocation,
        end_address: inputValues.endLocation,
        earliest_start: timeToSeconds(getHoursMins(inputValues.startTime)),
        latest_end: timeToSeconds(getHoursMins(inputValues.endTime)),
        break: {
          earliest: inputValues.startBreak
            ? timeToSeconds(getHoursMins(inputValues.startBreak))
            : null,
          latest: inputValues.endBreak
            ? timeToSeconds(getHoursMins(inputValues.endBreak))
            : null,
          duration: inputValues.breakDuration
            ? Number(inputValues.breakDuration)
            : null
        }
      }
    ];
  }

  // Update store
  // API call
  if (vehicle) {
    handleClose();
    dispatch(setLoadingProject(true));

    API.patch(
      `/v1/projects/${project.id}/vehicles/${vehicle.vehicle_id}`,
      bodyArr[0]
    )
      .then(res => {
        const newVehicles = vehicles.map(vehicle => {
          const index = res.data.findIndex(
            (resVehicle, i) => vehicle.vehicle_id === resVehicle.vehicle_id
          );
          if (index !== -1) {
            return { ...res.data[index], selected: true };
          } else {
            return vehicle;
          }
        });

        dispatch(setVehicles(newVehicles));

        dispatch(
          setSelectedVehicles(
            newVehicles
              .filter(vehicle => vehicle.selected)
              .map(vehicle => vehicle.vehicle_id)
          )
        );

        // Update solution
        if (solution) {
          const newSolution = { ...solution };
          res.data.forEach(vehicle => {
            if (vehicle.routes) {
              newSolution.routes.push(...vehicle.routes);
            }
          });
          dispatch(setSolution(newSolution));
        }

        handleClose();
      })
      .catch(error => {
        console.log(error);
        dispatch(
          enqueueSnackbar({
            message: "Something went wrong, please refresh the page."
          })
        );
      })
      .finally(() => {
        dispatch(setLoadingProject(false));
      });
  } else {
    handleClose();
    dispatch(setLoadingProject(true));

    API.post(`/v1/projects/${project.id}/vehicles/`, bodyArr)
      .then(res => {
        console.log(res);
        console.log(res.data);
        const newVehicles = res.data.vehicles;
        const newRoutes = res.data.empty_routes;

        handleAdd(newVehicles, newRoutes);
      })
      .catch(error => {
        dispatch(setLoadingProject(false));
        dispatch(
          enqueueSnackbar({
            message: "Something went wrong, please refresh the page."
          })
        );
        console.log(error);
      })
      .finally(() => {
        dispatch(setLoadingProject(false));
      });
  }

  const handleAdd = (newVehicles, newRoutes) => {
    // Update vehicle types
    // Update store
    const finalVehicles = [...newVehicles];
    const selectedVehicles = newVehicles
      .filter(vehicle => vehicle.selected)
      .map(vehicle => vehicle.vehicle_id);

    dispatch(setSelectedVehicles(selectedVehicles));

    dispatch(setVehicles(finalVehicles));

    let newSolution;

    if (solution) {
      newSolution = { ...solution };
      newSolution.routes = newSolution.routes.map(route => {
        const possibleNewRoute = newRoutes.find(
          newRoute => newRoute.id === route.id
        );
        if (possibleNewRoute) {
          newRoutes = newRoutes.filter(
            newRoute => newRoute.id !== possibleNewRoute.id
          );
          return possibleNewRoute;
        } else if (selectedVehicles.includes(route.vehicle_id)) {
          return { ...route, selected: true };
        } else return route;
      });
      if (newRoutes.length) {
        newSolution.routes = [...newSolution.routes, ...newRoutes];
      }
      dispatch(setSolution(newSolution));
    }

    dispatch(setExtendableSaveDisabled(true));
  };

  dispatch(setExtendableSaveDisabled(true));
};

/* setSelectedVehiclesChange @params -> selectedVehicles: [vehlceId<String>],  mode: String */

export const setSelectedVehiclesChange =
  (selectedVehicles, mode) => (dispatch, getState) => {
    const selectedVehiclesList = Array.isArray(selectedVehicles)
      ? selectedVehicles
      : [selectedVehicles];

    //Check if we have emptyArray just go out from action and not make unnecessary requests;
    if (selectedVehiclesList.length < 1) {
      return;
    }

    const project = getState().project;
    const vehicles = project.vehicles;
    const solution = project.solution;

    const selectedVehicleIds = vehicles
      .filter(
        ({ vehicle_id, type_id }) =>
          selectedVehiclesList.includes(type_id.id) && vehicle_id
      )
      .map(({ vehicle_id }) => vehicle_id);
    const selectedVehiclesRequestBody = selectedVehiclesList.map(type_id => {
      return {
        type_id
      };
    });

    dispatch(setLoadingProject(true));

    const handleAdd = (newVehicles, newRoutes) => {
      const finalVehicles = [...newVehicles];
      const selectedVehicles = newVehicles
        .filter(vehicle => vehicle.selected)
        .map(vehicle => vehicle.vehicle_id);

      dispatch(setSelectedVehicles(selectedVehicles));

      dispatch(setVehicles(finalVehicles));

      let newSolution;

      if (solution) {
        newSolution = { ...solution };
        newSolution.routes = newSolution.routes.map(route => {
          const possibleNewRoute = newRoutes.find(
            newRoute => newRoute.id === route.id
          );
          if (possibleNewRoute) {
            newRoutes = newRoutes.filter(
              newRoute => newRoute.id !== possibleNewRoute.id
            );
            return possibleNewRoute;
          } else if (selectedVehicles.includes(route.vehicle_id)) {
            return { ...route, selected: true };
          } else return route;
        });
        if (newRoutes.length) {
          newSolution.routes = [...newSolution.routes, ...newRoutes];
        }
        dispatch(setSolution(newSolution));
      }
    };

    if (mode === "enable") {
      return API.post(
        `/v1/projects/${project.id}/vehicles/`,
        selectedVehiclesRequestBody
      )
        .then(res => {
          const newVehicles = res.data.vehicles;
          const newRoutes = res.data.empty_routes;
          handleAdd(newVehicles, newRoutes);
        })
        .catch(error => {
          dispatch(setLoadingProject(false));
          dispatch(
            enqueueSnackbar({
              message: "Something went wrong, please refresh the page."
            })
          );
          console.log(error);
        })
        .finally(() => {
          dispatch(setLoadingProject(false));
        });
    } else {
      return API.delete(`/v1/projects/${project.id}/vehicles/`, {
        data: selectedVehiclesRequestBody
      })
        .then(res => {
          // set new vehicles for count in the top of vehicles icon
          const newSelected = project.selectedVehicles.filter(
            vehicleId => !selectedVehicleIds.includes(vehicleId)
          );

          const vehicles = project.vehicles;

          // filtering over all vehicles and remove deleted from them
          const newVehicles = vehicles.filter(
            vehicle => !selectedVehiclesList.includes(vehicle?.type_id?.id)
          );

          const newSolution = { ...solution };
          // When we disable vehicle all routes related with vehicle must go in unscheduled ~
          if (project.solution) {
            newSolution.routes.forEach(route => {
              // DO this when vehicle disabled.
              // Used split method because routes with backhaul or reload comes with vehicle_id added _ and index for that for instance {vehicle_id}_1,_2 etc.
              if (selectedVehicleIds.includes(route.vehicle_id.split("_")[0])) {
                if (route.stops.length > 2) {
                  newSolution.unassigned.stops = [
                    ...newSolution.unassigned.stops,
                    ...route.stops.filter(
                      stop => !["break", "start", "end"].includes(stop.type)
                    )
                  ];
                }
                route.stops = route.stops.filter(stop =>
                  ["start", "end"].includes(stop.type)
                );
                route.selected = false;
              }
            });

            newSolution.unassigned_stops_count =
              newSolution.unassigned.stops.length;
          }

          dispatch(
            setProject({
              ...project,
              solution: solution ? newSolution : null,
              vehicles: newVehicles
            })
          );

          // setVehicles(newVehicles);

          dispatch({
            type: SELECTED_VEHICLES,
            arr: newSelected
          });

          // if (project.solution) {
          //   const newSolution = { ...project.solution };

          //   const routesToRemove = newSolution.routes.filter((route, i) => {
          //     if (selectedVehicleIds.includes(route.vehicle_id.split("_")[0])) {
          //       return true;
          //     }
          //     return false;
          //   });
          //   if (routesToRemove.length) {
          //     const tasksToUnassign = [];
          //     const routesToRemoveIds = routesToRemove.map(route => route.id);
          //     routesToRemove.forEach(route => {
          //       route.stops.forEach(stop => {
          //         if (
          //           stop.type !== "start" &&
          //           stop.type !== "end" &&
          //           stop.type !== "break"
          //         ) {
          //           tasksToUnassign.push(stop);
          //           return false;
          //         } else {
          //           return true;
          //         }
          //       });
          //     });
          //     newSolution.routes = newSolution.routes.filter(
          //       route => !routesToRemoveIds.includes(route.id)
          //     );
          //     newSolution.unassigned.stops = [
          //       ...newSolution.unassigned.stops,
          //       ...tasksToUnassign
          //     ];
          //     dispatch(setSolution(newSolution));
          //   }
          // }
        })
        .catch(error => {
          dispatch(setLoadingProject(false));
          dispatch(
            enqueueSnackbar({
              message: "Something went wrong, please refresh the page."
            })
          );
          console.log(error);
        })
        .finally(() => {
          dispatch(setLoadingProject(false));
        });
    }
  };

export const handleStopAdd =
  (body, { quantity, track, callback }) =>
    (dispatch, getState) => {
      const {
        project,
        project: { solution }
      } = getState();

      API.post(`/v1/projects/${project.id}/services`, body)
        .then(res => {
          track &&
            mixpanelWrapper.track("Stop Add", {
              Uploaded: false
            });

          const newProject = { ...project };
          let vrpStops = [...newProject.vrp.stops];

          const isFirstStop = isEmptyObj(vrpStops[0]);
          vrpStops = [
            ...(isFirstStop
              ? vrpStops.slice(0, 1)
              : isEmptyObj(vrpStops[vrpStops.length - 1])
                ? vrpStops.slice(0, -1)
                : vrpStops)
          ];

          if (isFirstStop) {
            vrpStops = [res.data, ...vrpStops];
          } else {
            vrpStops = [...vrpStops, res.data];
          }

          newProject.vrp.stops = vrpStops;
          if (solution) {
            const newSolution = { ...solution };
            newSolution.unassigned.stops = [
              ...newSolution.unassigned.stops,
              res.data
            ];
            setSolution(newSolution);
          }
          setProject(newProject);
          callback && callback(undefined, res.data);
        })
        .catch(err => {
          if (err && err.status === 400) {
            dispatch(
              setInfoDialog({
                visible: true,
                title: "Operation not possible",
                body: err.data.message
              })
            );
          } else {
            dispatch(
              enqueueSnackbar({
                message: "Something went wrong, couldn't update stop"
              })
            );
          }
          callback && callback(err);
          console.log(err);
        })
        .finally(() => {
          //callback && callback();
          dispatch(setLoadingProject(false));

          dispatch(setLoadingUnscheduled(false));
        });
    };

// get stop vrp
// delete stop
// get stop route/unscheduled
// delete stop
// if sheduled update route in api
// revert on catch
export const deleteStop = stop => (dispatch, getState) => {
  const project = getState().project;
  const solution = getState().project.solution;
  const editId = getState().extendable.id;
  let isEditing = editId && editId === stop.id;

  let index;
  let routeIndex;
  let route;
  let newSolution;

  if (isEditing) {
    dispatch(setExtendableType(null));
    dispatch(setExtendableId(null));
  }

  const newProject = { ...project };
  const taskIndex = project.vrp.stops.findIndex(el => el.id === stop.id);
  newProject.vrp.stops = newProject.vrp.stops.filter(el => el.id !== stop.id);
  dispatch(setProject(newProject));

  if (solution) {
    let routeId = solution.routes.find((route, i) => {
      index = route.stops.findIndex(el => el.id === stop.id);
      if (index !== -1) {
        routeIndex = i;
        return true;
      }
      return false;
    })?.id;

    newSolution = { ...solution };
    if (routeId) {
      const vehicles = getState().project.vehicles;
      route = solution.routes[routeIndex];
      if (stop.type === "break") {
        const newVehicles = vehicles.map(vehicle =>
          route.vehicle_id === vehicle.vehicle_id
            ? { ...vehicle, break: null }
            : vehicle
        );

        dispatch(setVehicles(newVehicles));
      }
      dispatch(setChangedVehicles(route.vehicle_id));

      newSolution.routes[routeIndex].stops.splice(index, 1);

      dispatch(setSolution(newSolution));
    } else {
      index = project.solution.unassigned.stops.findIndex(
        el => stop.id === el.id
      );

      newSolution.unassigned.stops.splice(index, 1);

      dispatch(setSolution(newSolution));
    }
  }

  stop.id &&
    API.delete(`/v1/projects/${project.id}/services/${stop.id}`)
      .then(res => {
        if (route) {
          const solutionWithNewETAs = { ...newSolution };
          solutionWithNewETAs.routes[routeIndex] = res.data;
          dispatch(setSolution(solutionWithNewETAs));
          sendReorderWSMessage(route.id);
        }
      })
      .catch(error => {
        const revertedProject = { ...getState().project };
        revertedProject.vrp.stops.splice(taskIndex, 0, stop);
        if (routeIndex !== undefined) {
          solution.routes[routeIndex].stops.splice(index, 0, stop);
        } else {
          if (solution) {
            solution.unassigned.stops.splice(index - 1, 0, stop);
          }
        }
        dispatch(setProject(revertedProject));
        dispatch(setSolution(solution));
        dispatch(
          enqueueSnackbar({
            message: "Something went wrong, couldn't delete stop"
          })
        );
        console.log(error);
      });
};

// Delete selected stops
export const deleteStops =
  (selectedStops, fromRoute = false) =>
    (dispatch, getState) => {
      const project = getState().project;
      //const vehicles = getState().project.vehicles;
      dispatch(setInitialProjectLoading(true));
      dispatch(setExtendableType(null));
      dispatch(setExtendableId(null));
      if (fromRoute) {
        dispatch(setLoadingRoutesList(true));
      }
      selectedStops &&
        API.delete(`/v1/projects/${project.id}/services/batch`, {
          data: selectedStops
        })
          .then(res => {
            const newProject = { ...project };
            //When we have solution copy solution when we have not solution set null (Draft route before optimize)
            let newSolution = !!project.solution ? { ...project.solution } : null;

            if (!!newSolution) {
              selectedStops.forEach(stopId => {
                //Get modified route Index
                const routeIndex = newSolution.routes.findIndex(
                  route => route.stops.findIndex(el => el.id === stopId) >= 0
                );
                if (routeIndex >= 0) {
                  const vehicles = getState().project.vehicles;
                  const route = newSolution.routes[routeIndex];
                  const stop = route.stops.find(stop => stop.id === stopId);

                  if (stop.type === "break") {
                    const newVehicles = vehicles.map(vehicle =>
                      route.vehicle_id === vehicle.vehicle_id
                        ? { ...vehicle, break: null }
                        : vehicle
                    );

                    dispatch(setVehicles(newVehicles));
                  }
                  dispatch(setChangedVehicles(route.vehicle_id));

                  sendReorderWSMessage(route.id);
                }
              });

              newSolution.unassigned.stops = newSolution.unassigned.stops.filter(
                stop => !selectedStops.includes(stop.id)
              );
              newSolution.unassigned_stops_count =
                newSolution.unassigned.stops.length;

              //Change old routes with new one
              if (res.data) {
                newSolution = cleanSolutionWithNewRoutes(newSolution, res.data);
                //Set recalculated routes form API
                res.data.forEach(route => {
                  const routeIndex = newSolution.routes.findIndex(
                    currentRoute => currentRoute.id === route.id
                  );
                  newSolution.routes[routeIndex] = route;
                });

                if (findRouteIndexById(project.showingRouteId, newSolution) < 0) {
                  if (res.data[0].stops.length > 2) {
                    newProject.showingRouteId = res.data[0].id;
                  }
                }
              } else {
                if (newSolution.routes) {
                  newSolution.routes = newSolution.routes.map(route =>
                  // Remove selected stops from current routes
                  ({
                    ...route,
                    stops:
                      route.stops.length > 2
                        ? route.stops.filter(
                          stop => !selectedStops.includes(stop.id)
                        )
                        : route.stops
                  })
                  );
                }
              }
            }
            newProject.vrp.stops = newProject.vrp.stops.filter(
              stop => !selectedStops.includes(stop.id)
            );
            // If we have not solution don't to it case when we in draft mode(before optimize)
            if (!!newSolution) {
              newProject.solution = newSolution;
            }
            dispatch(setProject(newProject));
            dispatch(setInitialProjectLoading(false));

            if (fromRoute) {
              dispatch(setDragSelectionByArr("", []));
              dispatch(setLoadingRoutesList(false));
            }
            dispatch(
              enqueueSnackbar({
                message: `${selectedStops.length} stop${makeTextPlural(
                  selectedStops.length
                )} deleted.`
              })
            );
          })
          .catch(error => {
            dispatch(
              enqueueSnackbar({
                message: "Something went wrong, couldn't delete stop"
              })
            );
            setShowingRouteId("");
            dispatch(setProject(project));
            dispatch(setInitialProjectLoading(false));
            if (fromRoute) {
              dispatch(setLoadingRoutesList(false));
            }
            console.log(error);
          });
    };

export const deleteUnscheduledSelectedStops =
  selectedStops => (dispatch, getState) => {
    const project = getState().project;

    dispatch(setLoadingUnscheduled(true));
    selectedStops &&
      API.delete(`/v1/projects/${project.id}/services/batch`, {
        data: selectedStops
      })
        .then(res => {
          const newProject = { ...project };

          newProject.vrp.stops = newProject.vrp.stops.filter(
            stop => !selectedStops.includes(stop.id)
          );
          if (newProject.solution) {
            newProject.solution.unassigned.stops =
              newProject.solution.unassigned.stops.filter(
                stop => !selectedStops.includes(stop.id)
              );
            newProject.solution.unassigned_stops_count =
              newProject.solution.unassigned.stops.length;
          }
          dispatch(setProject(newProject));
          dispatch(setManualDragSelection("unassigned", []));
          dispatch(
            enqueueSnackbar({
              message: `${selectedStops.length} stop${makeTextPlural(
                selectedStops.length
              )} deleted.`
            })
          );
        })
        .catch(error => {
          dispatch(
            enqueueSnackbar({
              message: "Something went wrong, couldn't delete stop"
            })
          );
          dispatch(setProject(project));
          dispatch(setInitialProjectLoading(false));
          console.log(error);
        })
        .finally(() => {
          dispatch(setLoadingUnscheduled(false));
        });
  };

// Handle stop changes
export const saveStopChanges =
  (stop, inputValues, isUnassigned, isTimeWindowSet, routeId) =>
    (dispatch, getState) => {
      dispatch(setLoadingStop(true));
      const project = getState().project;
      const editId = stop.id;
      dispatch(setLoadingProject(true));
      const { numberVal } = getFormattedPhoneAndCountry(
        inputValues.phoneNumber || ""
      );

      const noInitialTimeWindow = !(
        stop.time_windows && stop.time_windows.length
      );

      let newStop = { ...stop };
      const {
        type,
        name,
        phoneNumber,
        email,
        notes,
        deliveryStart,
        deliveryEnd,
        duration,
        load,
        units,
        weight,
        volume,
        highPriority,
        checkboxSignature,
        checkboxBarcode,
        checkboxPhoto,
        checkboxAge,
        tags
      } = inputValues;

      newStop = {
        ...newStop,
        disallowed_vehicles: newStop.disallowed_vehicles || [],
        images: newStop.images || [],
        required_skills: newStop.required_skills || [],
        type,
        units,
        weight,
        volume,
        priority: highPriority ? 1 : 5,
        duration: duration ? Number(duration) : null,
        size: [0, 0, 0],
        quantity: !load ? null : Number(load),
        information: {
          ...newStop.information,
          recipient_name: name,
          recipient_phone:
            numberVal !== inputValues.phoneCountry && phoneNumber.length > 1
              ? phoneNumber
              : null,
          notes,
          email
        },
        requirements: {
          signature: checkboxSignature,
          barcode: checkboxBarcode,
          picture: checkboxPhoto,
          age_verification: checkboxAge
        },
        tags,
        time_windows:
          !isTimeWindowSet && noInitialTimeWindow
            ? []
            : [
              {
                earliest: timeToSeconds(getHoursMins(deliveryStart)),
                latest: timeToSeconds(getHoursMins(deliveryEnd))
              }
            ]
      };

      API.patch(`v1/projects/${project.id}/services/${newStop.id}`, newStop)
        .then(res => {
          if (routeId && client) {
            client.send(
              JSON.stringify({
                type: "stop_update",
                stop: { ...newStop },
                route_id: routeId
              })
            );
          }

          const newProject = { ...project };
          newProject.vrp.stops[
            newProject.vrp.stops.findIndex(el => el.id === editId)
          ] = newStop;
          // console.log(newProject);
          const newData = Array.isArray(res.data)
            ? res.data.map(route => route?.stops?.find(el => el.id === editId))[0]
            : res.data;
          // console.log(res.data);
          if (newProject.solution) {
            const { type, priority, duration, size, information, requirements } =
              newStop;

            const timeWindows = {
              earliest: timeToSeconds(getHoursMins(deliveryStart)),
              latest: timeToSeconds(getHoursMins(deliveryEnd))
            };

            const stopData = {
              type,
              priority,
              duration,
              size,
              information,
              requirements,
              time_windows: [timeWindows],
              tags
            };

            stopData.units = newData.units;
            stopData.size = newData.size;
            //CASE WHEN STOP IS SCHEDULED
            if (!isUnassigned) {
              const newSolution = { ...newProject.solution };

              // When relaods (in past - backhaul) is allowed and when change stop duratiion the API sends us array of changed routes in other cases API just sends us changed stop
              if (Array.isArray(res.data)) {
                const newRoutes = res.data;
                newRoutes.forEach((newRoute, newRouteIndex) => {
                  const routeIndex = newProject.solution.routes.findIndex(
                    route => {
                      return route.id === newRoute.id;
                    }
                  );

                  dispatch(
                    setChangedVehicles(newSolution.routes[routeIndex].vehicle_id)
                  );

                  newSolution.routes[routeIndex] = res.data[newRouteIndex];
                });
              } else {
                const routeIndex = newProject.solution.routes.findIndex(el =>
                  el.stops.some(stop => stop.id === editId)
                );

                // Count the change
                dispatch(
                  setChangedVehicles(newSolution.routes[routeIndex].vehicle_id)
                );

                const stopIndex = newSolution.routes[routeIndex].stops.findIndex(
                  el => el.id === editId
                );

                let stop = newSolution.routes[routeIndex].stops[stopIndex];

                stop = { ...stop, ...stopData };

                const newRoute = {
                  ...newSolution.routes[routeIndex],
                  stops: newSolution.routes[routeIndex].stops.map((task, i) =>
                    i === stopIndex ? stop : task
                  )
                };
                newSolution.routes[routeIndex] = newRoute;
              }
              newProject.solution = newSolution;
            } else {
              const stopIndex = newProject.solution.unassigned.stops.findIndex(
                el => el.id === editId
              );
              let stop = newProject.solution.unassigned.stops[stopIndex];

              stop = { ...stop, ...stopData, time_windows: newStop.time_windows };

              newProject.solution.unassigned.stops[stopIndex] = stop;
            }
            const taskIndex = newProject.vrp.stops.findIndex(
              task => task.id === editId
            );

            newProject.vrp.stops[taskIndex] = newData;
          }
          dispatch(setProject(newProject));
          dispatch(setExtendableSaveDisabled(true));
          dispatch(setHoverStop(null));

          if (getState().project.status !== "published") {
            dispatch(setExtendableType(null));
            dispatch(setExtendableId(null));
          }

          if (isUnassigned && project.status !== "preparing")
            dispatch(reopenExtendable("unscheduled"));
          dispatch(setLoadingStop(false));
          dispatch(setLoadingProject(false));
        })
        .catch(error => {
          dispatch(
            enqueueSnackbar({
              message: "Something went wrong, couldn't update stop"
            })
          );
          console.log(error);
          dispatch(setLoadingStop(false));
          dispatch(setLoadingProject(false));
        });
    };
